import HelperMixin from '../../Shared/HelperMixin.vue';

const VUEXY_COLORS = ['#7367f0', '#28c76f', '#ea5455', '#ff9f43', '#00cfe8', '#845adf', '#5d596c'];

export default {
    name: 'ComprehensiveReports',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى التقارير الشاملة');
            this.href('/Home');
            return;
        }
        await this.loadFilters();
        await this.refreshAll();
    },

    beforeDestroy() {
        this.destroyCharts();
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isFacilityCenterRole() && this.loginDetails.branchesId) return true;
            return false;
        },
        canExport() {
            return this.canViewPage && !this.isFacilityDepartmentManagerRole();
        },
        showCustomDates() {
            return this.FilterPeriod === 'custom';
        },
        cards() {
            const m = this.metrics || {};
            return [
                this.cardOf('إجمالي قيمة المخزون', this.fmtMoney(m.totalStockValue), 'tone-primary', 'ti ti-coin'),
                this.cardOf('إجمالي الصرف (الفترة)', this.fmtMoney(m.totalIssueValue), 'tone-success', 'ti ti-arrow-right'),
                this.cardOf('إجمالي التوريد (الفترة)', this.fmtMoney(m.totalReceiveValue), 'tone-info', 'ti ti-arrow-left'),
                this.cardOf('عدد الطلبات (الفترة)', this.fmtNum(m.ordersCount), 'tone-warning', 'ti ti-file-text'),
                this.cardOf('عدد العمليات النشطة', this.fmtNum(m.activeOpsCount), 'tone-secondary', 'ti ti-chart-arrows'),
                this.cardOf('التنبيهات (منتهي/قريب الانتهاء/منخفض)', this.fmtNum(m.alertsCount), 'tone-danger', 'ti ti-alert-triangle'),
            ];
        },
        financialRows() {
            const f = this.financial || {};
            return [
                { label: 'إجمالي قيمة الصرف', value: this.fmtMoney(f.totalIssueValue) },
                { label: 'إجمالي قيمة التوريد', value: this.fmtMoney(f.totalReceiveValue) },
                { label: 'الفرق (توريد − صرف)', value: this.fmtMoney(f.differenceValue) },
                { label: 'قيمة المخزون الحالي', value: this.fmtMoney(f.currentStockValue) },
            ];
        },
        statisticsRows() {
            const s = this.statistics || {};
            return [
                { label: 'عدد عمليات الصرف', value: this.fmtNum(s.issueOps) },
                { label: 'عدد عمليات التوريد', value: this.fmtNum(s.receiveOps) },
                { label: 'عدد عمليات الجرد', value: this.fmtNum(s.countSessions) },
                { label: 'عدد الأصناف منتهية الصلاحية', value: this.fmtNum(s.expiredCount) },
                { label: 'عدد الأصناف قريبة الانتهاء', value: this.fmtNum(s.nearExpiryCount) },
            ];
        },
    },

    data() {
        return {
            ScreenTitle: 'التقارير الشاملة',
            FilterPeriod: 'month',
            FilterFrom: '',
            FilterTo: '',
            FilterFacility: '',
            FilterCategory: '',

            branchesList: [],
            categoriesList: [],

            metrics: {},
            charts: {},
            financial: {},
            statistics: {},

            chartInstances: {},
        };
    },

    methods: {
        cardOf(title, value, toneClass, icon) {
            return { title, value, toneClass, icon };
        },

        fmtNum(v) {
            if (v == null) return '0';
            const n = Number(v);
            if (!Number.isFinite(n)) return '0';
            return new Intl.NumberFormat('en-US').format(n);
        },

        fmtMoney(v) {
            if (v == null) return '—';
            const n = Number(v);
            if (!Number.isFinite(n)) return '—';
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n);
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string'
                    ? err.response.data
                    : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadFilters() {
            try {
                const [b, c] = await Promise.all([
                    this.$http.GetAllBranches(),
                    this.$http.GetAllCategories(),
                ]);
                this.branchesList = (b && b.data && b.data.info) || [];
                this.categoriesList = (c && c.data && c.data.info) || [];
            } catch {
                this.branchesList = [];
                this.categoriesList = [];
            }
        },

        async refreshAll() {
            this.$blockUI.Start();
            try {
                const params = this.buildParams();
                const [m, ch, f, s] = await Promise.all([
                    this.$http.GetComprehensiveReportMetrics(params),
                    this.$http.GetComprehensiveReportChartsData(params),
                    this.$http.GetComprehensiveReportFinancialData(params),
                    this.$http.GetComprehensiveReportStatisticsData(params),
                ]);
                this.metrics = (m && m.data) || {};
                this.charts = (ch && ch.data) || {};
                this.financial = (f && f.data) || {};
                this.statistics = (s && s.data) || {};
                this.$nextTick(() => this.renderCharts());
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تحميل البيانات', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        buildParams() {
            return {
                period: this.FilterPeriod || 'month',
                from: this.showCustomDates ? this.FilterFrom : null,
                to: this.showCustomDates ? this.FilterTo : null,
                branchesId: this.FilterFacility,
                categoryId: this.FilterCategory,
            };
        },

        ResetFilters() {
            this.FilterPeriod = 'month';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.FilterFacility = '';
            this.FilterCategory = '';
            this.refreshAll();
        },

        destroyCharts() {
            Object.keys(this.chartInstances || {}).forEach((k) => {
                try { this.chartInstances[k] && this.chartInstances[k].destroy(); } catch (_) { /* noop */ }
            });
            this.chartInstances = {};
        },

        renderCharts() {
            if (typeof window.ApexCharts === 'undefined') return;
            this.destroyCharts();

            const c = this.charts || {};

            // 1) Trend (line)
            const trendEl = document.getElementById('compChartTrend');
            if (trendEl && c.trend && c.trend.length) {
                const labels = c.trend.map((x) => x.label);
                this.chartInstances.trend = new window.ApexCharts(trendEl, {
                    chart: { type: 'area', height: 300, toolbar: { show: false } },
                    series: [
                        { name: 'الصرف', data: c.trend.map((x) => Number(x.issueValue || 0)) },
                        { name: 'التوريد', data: c.trend.map((x) => Number(x.receiveValue || 0)) },
                    ],
                    xaxis: { categories: labels },
                    colors: [VUEXY_COLORS[2], VUEXY_COLORS[1]],
                    dataLabels: { enabled: false },
                    stroke: { curve: 'smooth', width: 2 },
                    legend: { position: 'top' },
                    tooltip: { y: { formatter: (v) => this.fmtMoney(v) } },
                });
                this.chartInstances.trend.render();
            }

            // 2) Top products (horizontal bar)
            const topEl = document.getElementById('compChartTopProducts');
            if (topEl && c.topProducts && c.topProducts.length) {
                this.chartInstances.top = new window.ApexCharts(topEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                    series: [{ name: 'الكمية المصروفة', data: c.topProducts.map((x) => Number(x.totalQty || 0)) }],
                    xaxis: { categories: c.topProducts.map((x) => x.productName || '—') },
                    colors: [VUEXY_COLORS[0]],
                    dataLabels: { enabled: false },
                });
                this.chartInstances.top.render();
            }

            // 3) By category (pie)
            const catEl = document.getElementById('compChartByCategory');
            if (catEl && c.byCategory && c.byCategory.length) {
                this.chartInstances.cat = new window.ApexCharts(catEl, {
                    chart: { type: 'pie', height: 320 },
                    series: c.byCategory.map((x) => Number(x.totalValue || 0)),
                    labels: c.byCategory.map((x) => x.categoryName || '—'),
                    colors: VUEXY_COLORS,
                    legend: { position: 'bottom' },
                    tooltip: { y: { formatter: (v) => this.fmtMoney(v) } },
                });
                this.chartInstances.cat.render();
            }

            // 4) By warehouse (bar)
            const whEl = document.getElementById('compChartByWarehouse');
            if (whEl && c.byWarehouse && c.byWarehouse.length) {
                this.chartInstances.wh = new window.ApexCharts(whEl, {
                    chart: { type: 'bar', height: 320, toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 4 } },
                    series: [{ name: 'القيمة', data: c.byWarehouse.map((x) => Number(x.totalValue || 0)) }],
                    xaxis: { categories: c.byWarehouse.map((x) => `${x.stockingName} (${x.branchName})`) },
                    colors: [VUEXY_COLORS[3]],
                    dataLabels: { enabled: false },
                    tooltip: { y: { formatter: (v) => this.fmtMoney(v) } },
                });
                this.chartInstances.wh.render();
            }
        },

        ExportExcel() {
            const url = this.$http.GetComprehensiveReportExportExcelUrl(this.buildParams());
            window.open(url, '_blank');
        },

        ExportPdf() {
            const url = this.$http.GetComprehensiveReportExportPdfUrl(this.buildParams());
            window.open(url, '_blank');
        },
    },
};
